import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputPassword from '@nxsys/components/AppInputPassword';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';
import { CompanyCreationTypeEnum } from '@nxsys/constants/AppEnums';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { AppState } from '@nxsys/store';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

export type HMRCSettingValues = {
  // Employer Settings
  taxDistrict: string;
  employerPayeReference: string;
  accountsOfficeReference: string;
  claimEmploymentAllowance: boolean;
  allowanceClaimableEmpAllowance: string;
  claimApprenticeshipLevy: boolean;
  eligibleForSmallEmpRelief: boolean;
  allowanceClaimableAppLevy: string;
  deductionRate: string;
  recoverableAmountsYTD: boolean;
  smpRecoveredYtd: string;
  sapRecoveredYtd: string;
  spbpRecoveredYtd: string;
  nicCompensationOnSmpYtd: string;
  nicCompensationOnSapYtd: string;
  nicCompensationOnSpbpYtd: string;
  cisDeductionSufferedYtd: string;
  appLevyClaimedYtd: string;
  empAllowanceClaimedYtd: string;
  sppRecoveredYtd: string;
  shppRecoveredYtd: string;
  sncpRecoveredYtd: string;
  nicCompensationOnSppYtd: string;
  nicCompensationOnShppYtd: string;
  nicCompensationOnSncpYtd: string;

  // RTI Settings
  enableRTI: boolean;
  userId: string;
  password: string;
  submitAsAgent: boolean;
  agentForename: string;
  agentSurname: string;
  agentAddressLine1: string;
  agentAddressLine2: string;
  agentCityTown: string;
  agentCountry: Option<string> | null;
  agentPostCode: string;
};

export type HMRCSettingFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => HMRCSettingValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<HMRCSettingFormRef>;
  initialValues: Partial<HMRCSettingValues>;
  creationType: CompanyCreationTypeEnum;
  stepNumber: number;
};

enum HMRCTabEnum {
  EMPLOYER_SETTINGS = 'EMPLOYER_SETTINGS',
  RTI_SETTINGS = 'RTI_SETTINGS',
}

const RTI_FIELDS: string[] = [
  'userId',
  'password',
  'submitAsAgent',
  'agentForename',
  'agentSurname',
  'agentAddressLine1',
  'agentAddressLine2',
  'agentCityTown',
  'agentCountry',
  'agentPostCode',
];

const HMRCSetting: React.FC<Props> = ({
  formRef,
  initialValues,
  creationType,
  stepNumber,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [form] = Form.useForm<HMRCSettingValues>();
  const watchValues = Form.useWatch([], form);

  const [activeTab, setActiveTab] = useState<HMRCTabEnum>(
    HMRCTabEnum.EMPLOYER_SETTINGS,
  );

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error: any) {
        // Switch to the tab that has the first error so user can see it
        const errorFields: string[] =
          error?.errorFields?.map((f: { name: string[] }) => f.name[0]) || [];
        const hasEmployerError = errorFields.some(
          (f) => !RTI_FIELDS.includes(f),
        );
        const hasRtiError = errorFields.some((f) => RTI_FIELDS.includes(f));

        if (hasEmployerError) {
          setActiveTab(HMRCTabEnum.EMPLOYER_SETTINGS);
        } else if (hasRtiError) {
          setActiveTab(HMRCTabEnum.RTI_SETTINGS);
        }

        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.employerSettings'],
        key: HMRCTabEnum.EMPLOYER_SETTINGS,
      },
      {
        tab: messages['common.rtiSettings'],
        key: HMRCTabEnum.RTI_SETTINGS,
      },
    ],
    [],
  );

  const handleTabChange = (key: HMRCTabEnum) => {
    setActiveTab(key);
  };

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="HMRCSetting">
      <StepTitle
        step={stepNumber}
        title="HMRC Setting"
        description={isBureau ? 'Set up your employer profile' : 'Set up your company profile'}
      />
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="space-y-[16px] mt-[24px]">
          <AppFormContainer
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            initialValues={{ enableRTI: false, ...initialValues }}
            layout="vertical"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            {/* Both tabs are always rendered so validateFields() covers all fields.
                Visibility is toggled via CSS to avoid unmounting. */}
            <div
              className={
                activeTab !== HMRCTabEnum.EMPLOYER_SETTINGS ? 'hidden' : ''
              }
            >
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconUserCircle" />
                <div className="text-[16px] font-semibold">
                  Employer Settings
                </div>
              </div>
              <Row gutter={24}>
                <Col sm={24} md={6}>
                  <Form.Item label="Tax District" name="taxDistrict">
                    <AppInput placeholder={messages['form.enter']} maxLength={3} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={9}>
                  <Form.Item
                    label="Employer Reference"
                    name="employerPayeReference"
                    rules={[
                      {
                        max: 10,
                        message:
                          'Employer Reference must be shorter than or equal to 10 characters',
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} maxLength={10} />
                  </Form.Item>
                </Col>
                <Col sm={24} md={9}>
                  <Form.Item
                    label="Accounts Office Reference"
                    name="accountsOfficeReference"
                    rules={[
                      {
                        max: 13,
                        message:
                          'Accounts Office Reference must be shorter than or equal to 13 characters',
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} maxLength={13} />
                  </Form.Item>
                </Col>
              </Row>
              <AppCard className="p-[16px]">
                <div className="flex items-center gap-x-[8px]">
                  <Form.Item name="claimEmploymentAllowance" className="mb-0">
                    <AppSwitch />
                  </Form.Item>
                  <div className="text-[14px] font-[500] text-[#5c656e]">
                    Claim Employment Allowance
                  </div>
                </div>
              </AppCard>
              <Row gutter={24} className="mt-[24px]">
                <Col sm={24} md={12}>
                  <AppCard className="p-[16px] h-full">
                    <div className="flex items-center gap-x-[8px]">
                      <Form.Item name="claimApprenticeshipLevy" className="mb-0">
                        <AppSwitch />
                      </Form.Item>
                      <div className="text-[14px] font-[500] text-[#5c656e]">
                        Claim Apprenticeship Levy
                      </div>
                    </div>
                  </AppCard>
                </Col>
                <Col sm={24} md={12}>
                  <AppCard className="p-[16px] h-full">
                    <div className="flex items-center gap-x-[8px]">
                      <Form.Item
                        name="eligibleForSmallEmpRelief"
                        className="mb-0"
                      >
                        <AppSwitch />
                      </Form.Item>
                      <div>
                        <div className="text-[14px] font-[500] text-[#5c656e]">
                          {messages['common.eligibleForSmallEmpRelief']}
                        </div>
                        {/* <div className="text-[12px] text-[#5c656e]">
                          {messages['common.eligibleForSmallEmpReliefHelp']}
                        </div> */}
                      </div>
                    </div>
                  </AppCard>
                </Col>
              </Row>
              {creationType === CompanyCreationTypeEnum.CONTINUE_PARTWAY && (
                <AppCard className="p-[16px] mt-[24px]">
                  <div className="flex items-center gap-x-[8px] mb-[16px]">
                    <Form.Item name="recoverableAmountsYTD" className="mb-0">
                      <AppSwitch />
                    </Form.Item>
                    <div className="text-[14px] font-[500] text-[#5c656e]">
                      Opening Balances
                    </div>
                  </div>
                  {!!watchValues?.recoverableAmountsYTD && (
                    <Row gutter={24} className="mt-[24px]">
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="Emp Allowance Claimed YTD"
                          name="empAllowanceClaimedYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="SMP Recovered YTD"
                          name="smpRecoveredYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="SPP Recovered YTD"
                          name="sppRecoveredYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="SAP Recovered YTD"
                          name="sapRecoveredYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="ShPP Recovered YTD"
                          name="shppRecoveredYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="SPBP Recovered YTD"
                          name="spbpRecoveredYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="SNCP Recovered YTD"
                          name="sncpRecoveredYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="NIC Compensation On SMP YTD"
                          name="nicCompensationOnSmpYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="NIC Compensation On SPP YTD"
                          name="nicCompensationOnSppYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="NIC Compensation On SAP YTD"
                          name="nicCompensationOnSapYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="NIC Compensation On ShPP YTD"
                          name="nicCompensationOnShppYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="NIC Compensation On SPBP YTD"
                          name="nicCompensationOnSpbpYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="NIC Compensation On SNCP YTD"
                          name="nicCompensationOnSncpYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="CIS Deduction Suffered YTD"
                          name="cisDeductionSufferedYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                      <Col sm={24} md={12}>
                        <Form.Item
                          label="App Levy Claimed YTD"
                          name="appLevyClaimedYtd"
                        >
                          <AppCurrencyInput placeholder={messages['form.enter']} />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </AppCard>
              )}
            </div>

            <div
              className={activeTab !== HMRCTabEnum.RTI_SETTINGS ? 'hidden' : ''}
            >
              <div className="flex items-center gap-x-[8px] mb-[16px]">
                <IconWrapper icon="IconUserSetting" />
                <div className="text-[16px] font-semibold">RTI Settings</div>
              </div>
              <Form.Item
                name="enableRTI"
                valuePropName="checked"
                className="mb-[16px]"
              >
                <AppCheckbox>Enable RTI</AppCheckbox>
              </Form.Item>
              {!!watchValues?.enableRTI && (
                <>
                  <Row gutter={24}>
                    <Col sm={24} md={12}>
                      <Form.Item label="User ID" name="userId">
                        <AppInput placeholder={messages['form.enter']} />
                      </Form.Item>
                    </Col>
                    <Col sm={24} md={12}>
                      <Form.Item label="Password" name="password">
                        <AppInputPassword
                          placeholder={messages['form.enter']}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <AppCard className="p-[16px]">
                    <div className="flex items-center gap-x-[8px]">
                      <Form.Item name="submitAsAgent" className="mb-0">
                        <AppSwitch />
                      </Form.Item>
                      <div className="text-[14px] font-[500] text-[#5c656e]">
                        Submit as Agent
                      </div>
                    </div>
                    {!!watchValues?.submitAsAgent && (
                      <Row gutter={24} className="mt-[24px]">
                        <Col sm={24} md={12}>
                          <Form.Item label="Forename" name="agentForename">
                            <AppInput placeholder="Enter forename" />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item label="Surname" name="agentSurname">
                            <AppInput placeholder="Enter surname" />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Address Line 1"
                            name="agentAddressLine1"
                          >
                            <AppInput placeholder="Enter address" />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Address Line 2"
                            name="agentAddressLine2"
                          >
                            <AppInput placeholder="Enter address" />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item label="City/Town" name="agentCityTown">
                            <AppInput placeholder="Enter city/town" />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item label="Country" name="agentCountry">
                            <AppSelect
                              placeholder="Select country"
                              options={countryList}
                            />
                          </Form.Item>
                        </Col>
                        <Col sm={24} md={12}>
                          <Form.Item
                            label="Post Code"
                            name="agentPostCode"
                            rules={[
                              postCodeValidationRule(messages, payrollCountry),
                            ]}
                          >
                            <AppInput
                              placeholder="Enter postcode"
                              upperCaseAll
                              onChange={(e) => {
                                form.setFieldValue(
                                  'agentPostCode',
                                  formatPostCode(
                                    e.target.value,
                                    watchValues?.agentCountry?.value || '',
                                  ),
                                );
                              }}
                              maxLength={8}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </AppCard>
                </>
              )}
            </div>
          </AppFormContainer>
        </div>
      </AppCardTabListContainer>
    </div>
  );
};

export default HMRCSetting;
